import { useState, useEffect } from 'react';
import { Users, CreditCard, TrendingUp, Store } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import api from '../services/api';

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statIcon, background: color + '20', color }}>
        <Icon size={22} />
      </div>
      <div>
        <div style={styles.statValue}>{value ?? '—'}</div>
        <div style={styles.statLabel}>{label}</div>
        {sub && <div style={styles.statSub}>{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [customers, synced, pending, error] = await Promise.all([
          api.get('/api/customers?limit=1'),
          api.get('/api/customers?cegid_sync_status=synced&limit=1'),
          api.get('/api/customers?cegid_sync_status=pending&limit=1'),
          api.get('/api/customers?cegid_sync_status=error&limit=1'),
        ]);
        setStats({
          total:   customers.data.pagination.total,
          synced:  synced.data.pagination.total,
          pending: pending.data.pagination.total,
          error:   error.data.pagination.total,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Datos de ejemplo para gráficas (en producción vendrán del backend)
  const registrosData = [
    { dia: 'Lun', clientes: 3 },
    { dia: 'Mar', clientes: 7 },
    { dia: 'Mié', clientes: 5 },
    { dia: 'Jue', clientes: 12 },
    { dia: 'Vie', clientes: 9 },
    { dia: 'Sáb', clientes: 15 },
    { dia: 'Dom', clientes: 4 },
  ];

  const tiendaData = [
    { tienda: 'Sucursal Centro',   clientes: 45 },
    { tienda: 'Sucursal Norte',    clientes: 32 },
    { tienda: 'Sucursal Sur',      clientes: 28 },
    { tienda: 'Portal web',        clientes: 67 },
  ];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Dashboard</h1>
        <p style={styles.subtitle}>Resumen del programa de lealtad 2CAP</p>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <StatCard icon={Users}      label="Total clientes"         value={stats?.total}   color="#1a1a2e" />
        <StatCard icon={CreditCard} label="Sincronizados Cegid"    value={stats?.synced}  color="#2e7d32" />
        <StatCard icon={TrendingUp} label="Pendientes de sync"     value={stats?.pending} color="#f57c00" />
        <StatCard icon={Store}      label="Errores de sync"        value={stats?.error}   color="#c62828" />
      </div>

      {/* Gráficas */}
      <div style={styles.chartsGrid}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Registros esta semana</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={registrosData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="dia" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line type="monotone" dataKey="clientes" stroke="#1a1a2e" strokeWidth={2} dot={{ fill: '#dc1414' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Clientes por tienda</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tiendaData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis dataKey="tienda" type="category" tick={{ fontSize: 11 }} width={110} />
              <Tooltip />
              <Bar dataKey="clientes" fill="#dc1414" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: { marginBottom: '28px' },
  title: { fontSize: '24px', fontWeight: 700, margin: '0 0 4px', color: '#1a1a1a' },
  subtitle: { fontSize: '14px', color: '#888', margin: 0 },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px', marginBottom: '24px',
  },
  statCard: {
    background: '#fff', borderRadius: '12px', padding: '20px 24px',
    display: 'flex', alignItems: 'center', gap: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #eee',
  },
  statIcon: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statValue: { fontSize: '28px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1 },
  statLabel: { fontSize: '13px', color: '#888', marginTop: '4px' },
  statSub: { fontSize: '12px', color: '#aaa', marginTop: '2px' },
  chartsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '16px',
  },
  chartCard: {
    background: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #eee',
  },
  chartTitle: { fontSize: '15px', fontWeight: 600, margin: '0 0 16px', color: '#1a1a1a' },
};
